import React, { createContext, useContext, useMemo, useState } from "react";
import { DEFAULT_COLORS, DEFAULT_CONFIG } from "../../config";

export const ConfigContext = createContext({
  config: {
    ...DEFAULT_CONFIG,
  },
  colors: {
    ...DEFAULT_COLORS,
  },
  patchConfigOverride: () => {},
  patchColorOverride: () => {},
  resetAllOverrides: () => {},
});

/**
 *
 * @param {{children: any, initialConfig: import("../../config").ConfigObject}} param0
 * @returns
 */
export function ConfigProvider({ children, initialConfig }) {
  const [configOverrides, setConfigOverrides] = useState({});
  const [colorOverrides, setColorOverrides] = useState({});

  /**
   *
   * @param {keyof import("../../types/config").ConfigObject} key
   * @param {string | number | boolean} value
   */
  function patchConfigOverride(key, value) {
    if (!(key in DEFAULT_CONFIG)) {
      console.warn(
        `${key} is not a valid override key, valid override keys are:`,
        Object.keys(DEFAULT_CONFIG).filter(
          (key) => !["stackId", "userId", "entitlements"].includes(key),
        ),
      );
      return;
    }

    switch (key) {
      // match the property the user is attempting to override. Some aren't or
      // aren't yet supported
      case "colors":
        console.error(
          "overriding colors with patchConfigOverride is not supported, use patchColorOverride",
        );
        break;
      case "text":
        console.error(
          "overriding text with patchConfigOverride is not supported",
        );
        break;
      case "entitlements":
        console.error(
          "overriding entitlements with patchConfigOverride is not supported",
        );
        break;
      case "stackId":
        console.error(
          "overriding stackId with patchConfigOverride is not supported",
        );
        break;
      case "userId":
        console.error(
          "overriding userId with patchConfigOverride is not supported",
        );
        break;
      case "leadContext":
        console.error(
          "overriding leadContext with patchConfigOverride is not supported",
        );
        break;
      case "chatContext":
        // TODO: support this here?
        console.error(
          "overriding chatContext with patchConfigOverride is not supported, use setChatContext",
        );
        break;
      case "advSizes":
        console.error(
          "overriding advSizes with patchConfigOverride is not supported, configure advanced sizes from the dashboard",
        );
        break;
      case "overrideLanguage":
        console.error(
          "overriding language with patchConfigOverride is not supported, use setLanguageOverride",
        );
        break;
      case "directParams":
        // it's technically reasonable to try and set an object here, so if the
        // user tries to do so we stringify it for them
        let parsedValue = value;
        if (typeof parsedValue === "object") {
          parsedValue = JSON.stringify(parsedValue);
        }
        setConfigOverrides((prev) => {
          const res = { ...prev, directParams: parsedValue };
          if (parsedValue === undefined) {
            delete res.directParams;
          }
          return res;
        });
        break;
      default:
        setConfigOverrides((prev) => {
          const res = { ...prev, [key]: value };
          // if the value is undefined, remove the override
          if (value === undefined) {
            delete res[key];
          }
          return res;
        });
        break;
    }
  }

  /**
   * @param {keyof import("../../types/config").ColorValues} key
   * @param {string} value
   */
  function patchColorOverride(key, value) {
    if (!(key in DEFAULT_COLORS)) {
      console.warn(
        `${key} is not a valid color key. Supported color keys are: \n${Object.keys(DEFAULT_COLORS).join("\n")}`,
      );
    } else {
      setColorOverrides((prev) => {
        const res = {
          ...prev,
          [key]: value,
        };
        if (value === undefined) {
          delete res[key];
        }
        return res;
      });
    }
  }

  function resetAllOverrides() {
    setConfigOverrides(() => ({}));
    setColorOverrides(() => ({}));
  }

  /**
   * @type {import("../../config").ConfigObject}
   */
  const derivedConfig = useMemo(
    () => ({
      ...DEFAULT_CONFIG,
      ...initialConfig,
      ...configOverrides,
    }),
    [initialConfig, configOverrides],
  );

  const derivedColors = useMemo(
    () => ({ ...DEFAULT_COLORS, ...initialConfig.colors, ...colorOverrides }),
    [colorOverrides],
  );

  return (
    <ConfigContext.Provider
      value={{
        config: derivedConfig,
        colors: derivedColors,
        patchConfigOverride,
        patchColorOverride,
        resetAllOverrides,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error("useConfig must be used inside ConfigProvider");
  }
  return context;
}
